/**
 * ProduktCardContent Component
 * Displays Produkt (Product Fit) data in a card layout
 * Shows radar chart for target group fit and product fit indicator
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import { AnalysisChart } from '@/components/charts/AnalysisChart/AnalysisChart';
import { getFitIndicatorLabel, getFitIndicatorColor } from '@/types/produkt';
import type { ProduktFullData } from '@/types/produkt';
import type { ChartConfig } from '@/types/api';

export interface ProduktCardContentProps {
  data: ProduktFullData;
}

/**
 * Card content for Produkt displaying radar chart and fit indicator
 */
export function ProduktCardContent({
  data,
}: ProduktCardContentProps) {
  const { t } = useTranslation();
  const { main } = data;

  // Create radar chart config for Produktmerkmale
  const radarChartConfig = useMemo((): ChartConfig | null => {
    const merkmale = main.Produkt_Fit_Produktmerkmale;
    if (!merkmale || merkmale.length === 0) {
      return null;
    }

    const categories = merkmale.map(item => item.Produkt_Fit_Produktmerkmale_Merkmal_Name || '');
    const values = merkmale.map(item => item.Produkt_Fit_Produktmerkmale_Merkmal_Prozent || 0);

    return {
      type: 'radar',
      series: [{
        name: 'Produktmerkmale',
        data: values,
      }] as unknown as ChartConfig['series'],
      options: {
        chart: {
          type: 'radar',
          offsetY: -20,
          parentHeightOffset: 0,
        },
        grid: {
          padding: {
            top: -20,
            right: 0,
            bottom: -20,
            left: 0,
          },
        },
        xaxis: {
          categories: categories,
          labels: {
            show: true,
            style: {
              fontSize: '11px',
              colors: Array(categories.length).fill('#32373c'),
            },
          },
        },
        yaxis: {
          show: false,
          max: 100,
          tickAmount: 5,
        },
        colors: ['#FC03B0'], // Brand pink
        fill: {
          opacity: 0.25,
        },
        stroke: {
          width: 2,
        },
        markers: {
          size: 4,
          colors: ['#FC03B0'],
          strokeColors: '#fff',
          strokeWidth: 1,
        },
        plotOptions: {
          radar: {
            polygons: {
              strokeColors: '#e0e0e0',
              strokeWidth: 1,
              connectorColors: '#e0e0e0',
              fill: {
                colors: ['#fafafa', '#fff'],
              },
            },
          },
        },
        tooltip: {
          y: {
            formatter: (val: number) => `${val}%`,
          },
        },
        legend: {
          show: false,
        },
      } as ChartConfig['options'],
    };
  }, [main.Produkt_Fit_Produktmerkmale]);

  const fitIndicator = main.Produkt_Fit_Indikator || 0;
  const fitLabel = getFitIndicatorLabel(fitIndicator);
  const fitColor = getFitIndicatorColor(fitIndicator);

  return (
    <div className="space-y-3">
      {/* Product info header */}
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-[var(--color-charcoal)]">
          {main.Produkt_Fit_Produktname || '-'}
        </p>
        {main.Produkt_Fit_Produktlink && main.Produkt_Fit_Produktlink !== '[placeholder]' && (
          <a
            href={main.Produkt_Fit_Produktlink}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-[var(--color-primary-start)] hover:underline inline-flex items-center gap-1"
          >
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>

      {/* Radar chart - Produktmerkmale */}
      <div className="-mx-2 -my-4">
        {radarChartConfig ? (
          <AnalysisChart
            config={radarChartConfig}
            height={280}
          />
        ) : (
          <div className="h-[280px] flex items-center justify-center bg-[var(--glass-bg)] rounded-md mx-2 my-4">
            <span className="text-xs text-[var(--color-charcoal)]/50">{t('results.cards.produkt.card.noData')}</span>
          </div>
        )}
      </div>

      {/* Fit indicator - compact horizontal bar */}
      <div className="pt-3 border-t border-[var(--glass-border)]">
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm font-medium text-[var(--color-charcoal)]">
            {t('results.cards.produkt.card.productFit')}
          </span>
          <div className="flex items-center gap-3">
            {/* Visual scale bar */}
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map((level) => (
                <div
                  key={level}
                  className="w-6 h-3 first:rounded-l last:rounded-r transition-colors"
                  style={{
                    backgroundColor: level <= fitIndicator ? fitColor : '#e5e7eb',
                  }}
                />
              ))}
            </div>
            {/* Label */}
            <span
              className="text-sm font-bold min-w-[80px] text-right"
              style={{ color: fitColor }}
            >
              {fitLabel}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProduktCardContent;
